export type MeetingActionLifecycle = 'ACTIVE' | 'GRACE_PERIOD' | 'LOCKED' | 'CANCELLED';

export interface LifecycleMeetingInput {
  status: 'Active' | 'Cancelled';
  meeting_date: string;
  ends_at?: string | null;
  lifecycle?: MeetingActionLifecycle;
  grace_ends_on?: string | null;
}

export interface MeetingActionPolicy {
  editMeeting: boolean;
  saveQr: boolean;
  manualCheckIn: boolean;
  cancelMeeting: boolean;
  deleteMeeting: boolean;
  cohostMutation: boolean;
  expectedAttendeeMutation: boolean;
  attendanceCorrection: boolean;
  projector: boolean;
  attendanceAuditView: boolean;
  exportCsv: boolean;
  printAttendance: boolean;
}

function malaysiaDate(value: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kuala_Lumpur',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function addCalendarDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  const value = new Date(Date.UTC(year, month - 1, day + days));
  return value.toISOString().slice(0, 10);
}

function formatLifecycleDate(date: string): string {
  return new Intl.DateTimeFormat('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kuala_Lumpur',
  }).format(new Date(`${date}T00:00:00+08:00`));
}

/**
 * Prefer the authoritative lifecycle returned by Migration 034. The fallback
 * mirrors that helper only for Host UI continuity when the operational read
 * fails and Meeting Detail falls back to the older Host state response.
 * PostgreSQL remains authoritative for every mutation.
 */
export function resolveMeetingLifecycle(
  meeting: LifecycleMeetingInput,
  now = new Date(),
): MeetingActionLifecycle {
  if (meeting.status === 'Cancelled') return 'CANCELLED';
  if (meeting.lifecycle) return meeting.lifecycle;

  const today = malaysiaDate(now);
  if (today <= meeting.meeting_date) return 'ACTIVE';
  return today <= addCalendarDays(meeting.meeting_date, 7) ? 'GRACE_PERIOD' : 'LOCKED';
}

export function meetingLifecycleLabel(
  meeting: LifecycleMeetingInput,
  lifecycle: MeetingActionLifecycle,
  now = new Date(),
): string {
  if (lifecycle === 'CANCELLED') return 'Dibatalkan';
  if (lifecycle === 'LOCKED') return 'Rekod Ditutup';
  if (lifecycle === 'GRACE_PERIOD') {
    const graceEndsOn = meeting.grace_ends_on ?? addCalendarDays(meeting.meeting_date, 7);
    return `Kemaskini Kehadiran Sehingga ${formatLifecycleDate(graceEndsOn)}`;
  }
  return malaysiaDate(now) < meeting.meeting_date ? 'Akan Datang' : 'Kehadiran Dibuka';
}

export function meetingActionPolicy(lifecycle: MeetingActionLifecycle): MeetingActionPolicy {
  const correctionWindowOpen = lifecycle === 'ACTIVE' || lifecycle === 'GRACE_PERIOD';
  return {
    editMeeting: correctionWindowOpen,
    saveQr: lifecycle === 'ACTIVE',
    manualCheckIn: correctionWindowOpen,
    cancelMeeting: correctionWindowOpen,
    deleteMeeting: correctionWindowOpen,
    cohostMutation: correctionWindowOpen,
    expectedAttendeeMutation: correctionWindowOpen,
    attendanceCorrection: correctionWindowOpen,
    projector: lifecycle === 'ACTIVE',
    attendanceAuditView: true,
    exportCsv: true,
    printAttendance: true,
  };
}
